import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Star, Angry, Lightbulb, Heart, Camera, Loader2, CheckCircle2, Send, X, ImageIcon, ArrowRight, Images } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackEvent } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/zdjtc3rc_Logo_Falo_com_o_dono-removebg-preview.png";

const GoogleG = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-3.2-.4-4.7H24v9h11.8c-.5 2.8-2.1 5.1-4.4 6.7v5.5h7.1c4.2-3.8 6.6-9.5 6.6-16.5z" />
    <path fill="#34A853" d="M24 46c6 0 11-2 14.5-5.4l-7.1-5.5c-2 1.3-4.5 2.1-7.4 2.1-5.7 0-10.5-3.8-12.2-9H4.5v5.7C8 41 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.8 28.2c-.4-1.3-.7-2.7-.7-4.2s.3-2.9.7-4.2v-5.7H4.5A22 22 0 0 0 2 24c0 3.6.9 6.9 2.5 9.9l7.3-5.7z" />
    <path fill="#EA4335" d="M24 10.8c3.2 0 6.1 1.1 8.4 3.3l6.3-6.3C34.9 4.1 30 2 24 2 15.4 2 8 7 4.5 14.1l7.3 5.7c1.7-5.2 6.5-9 12.2-9z" />
  </svg>
);

const GoogleWordmark = ({ className = 'text-3xl' }) => (
  <span className={`font-bold tracking-tight ${className}`} style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
    <span style={{ color: '#4285F4' }}>G</span>
    <span style={{ color: '#EA4335' }}>o</span>
    <span style={{ color: '#FBBC05' }}>o</span>
    <span style={{ color: '#4285F4' }}>g</span>
    <span style={{ color: '#34A853' }}>l</span>
    <span style={{ color: '#EA4335' }}>e</span>
  </span>
);

const SuccessScreen = ({ primaryColor, thankYouMessage, googleMapsUrl, isVip, poweredByRef, companyPlan, companySlug }) => {
  const bgColor = primaryColor || '#0A2647';
  const message = thankYouMessage || 'Muito obrigado pela sua contribuição. Vamos trabalhar para melhorar a cada dia.';
  const mapsUrl = (typeof googleMapsUrl === 'string' && googleMapsUrl.trim()) ? googleMapsUrl.trim() : null;

  const conviteLink = poweredByRef
    ? `https://www.falocomodono.com.br/convite/${poweredByRef}${companySlug ? `?from=${encodeURIComponent(companySlug)}` : ''}`
    : null;
  const fallbackLink = 'https://falocomodono.com.br/feira';

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6" 
      style={{ backgroundColor: bgColor }}
      data-testid="feedback-success-screen"
    >
      <div className="max-w-sm w-full text-center">
        <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-12 h-12 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-4">Obrigado!</h1>
        <p className="text-white/90 text-lg leading-relaxed">{message}</p>
        {mapsUrl && (
          <div className="mt-8 bg-white/20 backdrop-blur rounded-2xl p-5">
            <div className="bg-white rounded-xl py-4 px-5 mb-4 shadow-md" data-testid="google-review-badge">
              <div className="flex justify-center gap-1 mb-1.5">
                {[0, 1, 2, 3, 4].map(i => (
                  <Star key={i} className="w-5 h-5" fill="#FBBC05" stroke="none" />
                ))}
              </div>
              <p className="text-slate-500 text-[11px] font-semibold uppercase tracking-widest mb-0.5">Avalie no</p>
              <GoogleWordmark className="text-4xl" />
            </div>
            <p className="text-white/90 text-sm leading-relaxed mb-4">Sua avaliação foi incrível! Que tal compartilhar essa experiência no Google Maps também e nos ajudar a ser mais reconhecidos na nossa cidade?</p>
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2.5 bg-white text-slate-900 font-bold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all"
              data-testid="google-maps-redirect-btn"
            >
              <GoogleG className="w-5 h-5" />
              Avaliar no Google Maps
            </a>
          </div>
        )}

        {/* Sales Funnel CTA */}
        <div className="mt-8 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6" data-testid="sales-funnel-cta">
          <p className="text-white font-semibold text-base leading-relaxed mb-2">
            Tem uma empresa?
          </p>
          <p className="text-white/80 text-sm leading-relaxed mb-5">
            Ouça seus clientes antes que a reclamação vire 1 estrela. Teste o <span className="font-bold text-[#C5A059]">Falo Com O Dono</span> grátis por 30 dias, sem cartão e sem fidelidade.
          </p>
          <a
            href={conviteLink || fallbackLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] font-bold text-base px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all w-full"
            data-testid="conhecer-plano-free-btn"
          >
            <ArrowRight className="w-5 h-5" />
            Testar grátis por 30 dias
          </a>
        </div>

        <p className="text-white/40 text-xs mt-6">Você já pode fechar esta página.</p>
      </div>
    </div>
  );
};

const StarRating = ({ label, value, onChange, secondaryColor }) => (
  <div className="space-y-3">
    <Label className="text-base font-semibold text-slate-800">{label}</Label>
    <div className="flex gap-1 justify-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="p-1"
          data-testid={`rating-${label.toLowerCase()}-${star}`}
          style={{ minWidth: '52px', minHeight: '52px' }}
        >
          <Star
            className="w-11 h-11 transition-colors"
            style={{
              fill: star <= value ? secondaryColor : 'transparent',
              color: star <= value ? secondaryColor : '#d1d5db'
            }}
          />
        </button>
      ))}
    </div>
  </div>
);

const FeedbackForm = () => {
  const { slug } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [viewState, setViewState] = useState('form'); // 'form' | 'success' | 'suspended'
  const [error, setError] = useState(null);
  const [googleMapsUrl, setGoogleMapsUrl] = useState(null);
  
  const [customerName, setCustomerName] = useState('');
  const [customerWhatsapp, setCustomerWhatsapp] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  const [ratingFood, setRatingFood] = useState(0);
  const [ratingService, setRatingService] = useState(0);
  const [ratingAmbiance, setRatingAmbiance] = useState(0);
  const [message, setMessage] = useState('');
  const [photoUrls, setPhotoUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [complaintTags, setComplaintTags] = useState([]);

  // Default universal tags
  const DEFAULT_TAGS = {
    atendimento: ['Demora no Atendimento', 'Falta de Conhecimento sobre Produto/Serviço', 'Falta de Atenção', 'Erro no Pedido / Serviço', 'Atendimento Grosseiro'],
    produto: ['Não Atendeu Expectativa', 'Produto com Defeito', 'Produto em Falta', 'Apresentação Visual', 'Custo-Benefício'],
    ambiente: ['Limpeza / Higiene', 'Temperatura / Clima', 'Barulho / Acústica', 'Conforto / Estrutura', 'Organização / Sinalização']
  };

  const TAG_CATEGORIES = {
    atendimento: {
      label: 'O que houve com o Atendimento?',
      tags: (company?.settings?.custom_tags_atendimento?.length > 0 ? company.settings.custom_tags_atendimento : DEFAULT_TAGS.atendimento)
    },
    produto: {
      label: 'O que houve com o Produto/Serviço?',
      tags: (company?.settings?.custom_tags_produto?.length > 0 ? company.settings.custom_tags_produto : DEFAULT_TAGS.produto)
    },
    ambiente: {
      label: 'O que houve com o Ambiente?',
      tags: (company?.settings?.custom_tags_ambiente?.length > 0 ? company.settings.custom_tags_ambiente : DEFAULT_TAGS.ambiente)
    }
  };

  const toggleTag = (tag) => {
    setComplaintTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const tagsEnabled = company?.settings?.complaint_tags_enabled;
  const showServiceTags = tagsEnabled && ratingService > 0 && ratingService <= 3;
  const showFoodTags = tagsEnabled && ratingFood > 0 && ratingFood <= 3;
  const showAmbianceTags = tagsEnabled && ratingAmbiance > 0 && ratingAmbiance <= 3;

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await axios.get(`${API}/companies/${slug}`);
        setCompany(response.data);
        setError(null);
      } catch (err) {
        setError('Empresa não encontrada');
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, [slug]);

  const handlePhotoUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 3 - photoUrls.length;
    if (remaining <= 0) {
      toast.error('Limite de 3 fotos atingido');
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.info(`Apenas ${remaining} foto(s) adicionada(s) (limite: 3)`);
    }
    setUploading(true);
    const newUrls = [];
    for (const file of toUpload) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: Arquivo muito grande (máximo 5MB)`);
        continue;
      }
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newUrls.push(response.data.url);
      } catch {
        toast.error(`Erro ao enviar ${file.name}`);
      }
    }
    if (newUrls.length > 0) {
      setPhotoUrls(prev => [...prev, ...newUrls]);
      toast.success(`${newUrls.length} foto(s) anexada(s)!`);
    }
    setUploading(false);
    const cameraInput = document.getElementById('photo-camera');
    const galleryInput = document.getElementById('photo-gallery');
    if (cameraInput) cameraInput.value = '';
    if (galleryInput) galleryInput.value = '';
  }, [photoUrls]);

  const removePhoto = useCallback((index) => {
    setPhotoUrls(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!customerName || !customerWhatsapp || !customerEmail) {
      toast.error('Preencha seus dados de contato');
      return;
    }
    if (!feedbackType) {
      toast.error('Selecione o tipo de mensagem');
      return;
    }
    if (ratingFood === 0 || ratingService === 0 || ratingAmbiance === 0) {
      toast.error('Avalie todas as categorias');
      return;
    }
    if (!message.trim()) {
      toast.error('Escreva sua mensagem');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await axios.post(`${API}/feedbacks/${slug}`, {
        customer_name: customerName,
        customer_whatsapp: customerWhatsapp,
        customer_email: customerEmail,
        feedback_type: feedbackType,
        rating_food: ratingFood,
        rating_service: ratingService,
        rating_ambiance: ratingAmbiance,
        message: message,
        photo_url: photoUrls[0] || null,
        photo_urls: photoUrls.length > 0 ? photoUrls : null,
        complaint_tags: complaintTags.length > 0 ? complaintTags : null
      });
      setSubmitting(false);
      const avgRating = (ratingFood + ratingService + ratingAmbiance) / 3;
      trackEvent('feedback_submitted', {
        company_slug: slug,
        type: feedbackType,
        rating_avg: Number(avgRating.toFixed(1)),
        has_photo: photoUrls.length > 0,
        has_tags: complaintTags.length > 0,
      });
      try {
        if (res.data?.google_maps_redirect) {
          setGoogleMapsUrl(res.data.google_maps_redirect);
        }
      } catch { /* Safe fallback - maps redirect parse failed */ }
      setViewState('success');
    } catch (err) {
      if (err.response?.status === 503) {
        setViewState('suspended');
      } else if (err.response?.status === 403) {
        toast.error(err.response?.data?.detail || 'Esta empresa atingiu o limite de avaliações gratuitas neste mês.');
      } else if (err.response?.status === 429) {
        toast.error('Este estabelecimento atingiu o limite de avaliações do mês. Tente novamente no próximo mês.');
      } else {
        toast.error('Erro ao enviar. Tente novamente.');
      }
      setSubmitting(false);
    }
  }, [customerName, customerWhatsapp, customerEmail, feedbackType, ratingFood, ratingService, ratingAmbiance, message, photoUrls, slug]);

  const canSubmit = customerName && customerWhatsapp && customerEmail && 
                    feedbackType && ratingFood > 0 && ratingService > 0 && 
                    ratingAmbiance > 0 && message.trim();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2647]" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Empresa não encontrada</h1>
          <p className="text-slate-600">Verifique se o link está correto</p>
        </div>
      </div>
    );
  }

  const primaryColor = company.settings?.primary_color || '#0A2647';
  const secondaryColor = company.settings?.secondary_color || '#C5A059';
  const thankYouMessage = company.settings?.thank_you_message || 'Muito obrigado pela sua contribuição. Vamos trabalhar para melhorar a cada dia.';

  if (viewState === 'success') {
    return <SuccessScreen primaryColor={primaryColor} thankYouMessage={thankYouMessage} googleMapsUrl={googleMapsUrl} isVip={company?.is_vip} poweredByRef={company?.powered_by_ref} companyPlan={company?.plan} companySlug={company?.slug} />;
  }

  if (viewState === 'suspended') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="feedback-suspended-screen">
        <div className="max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-8 h-8 text-slate-400" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Sistema em manutenção momentânea</h1>
          <p className="text-slate-500 text-sm">Por favor, tente novamente em breve. Obrigado pela compreensão.</p>
        </div>
      </div>
    );
  }

  const companyLogo = company.settings?.logo_url 
    ? `${process.env.REACT_APP_BACKEND_URL}${company.settings.logo_url}` 
    : LOGO_URL;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="feedback-form-page">
      <header 
        className="sticky top-0 z-40 py-4 px-4 shadow-sm"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="max-w-lg mx-auto flex items-center justify-center gap-3">
          <img 
            src={companyLogo} 
            alt={company.name} 
            className="h-14 w-auto object-contain"
            data-testid="feedback-company-logo"
          />
          <span className="font-semibold text-white text-lg">{company.name}</span>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="max-w-lg mx-auto px-4 py-6 space-y-6" data-testid="feedback-form">
        
        {/* Dados de Contato */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Seus Dados</h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-slate-700">Nome</Label>
              <Input
                id="name"
                data-testid="input-name"
                placeholder="Seu nome"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="h-12 text-base mt-1"
              />
            </div>
            <div>
              <Label htmlFor="whatsapp" className="text-sm font-medium text-slate-700">WhatsApp</Label>
              <Input
                id="whatsapp"
                data-testid="input-whatsapp"
                placeholder="(00) 00000-0000"
                value={customerWhatsapp}
                onChange={(e) => setCustomerWhatsapp(e.target.value)}
                className="h-12 text-base mt-1"
                inputMode="tel"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">E-mail</Label>
              <Input
                id="email"
                data-testid="input-email"
                type="email"
                placeholder="seu@email.com"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                className="h-12 text-base mt-1"
                inputMode="email"
              />
            </div>
          </div>
        </div>

        {/* Tipo de Feedback */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Tipo de Mensagem</h2>
          <div className="flex gap-3">
            {[
              { type: 'complaint', label: 'Reclamação', icon: Angry, activeClass: 'bg-red-600 text-white border-red-600' },
              { type: 'suggestion', label: 'Sugestão', icon: Lightbulb, activeClass: 'bg-orange-500 text-white border-orange-500' },
              { type: 'praise', label: 'Elogio', icon: Heart, activeClass: 'bg-emerald-600 text-white border-emerald-600' },
            ].map(({ type, label, icon: Icon, activeClass }) => (
              <button
                key={type}
                type="button"
                data-testid={`feedback-type-${type}`}
                onClick={() => setFeedbackType(type)}
                className={`flex-1 flex flex-col items-center justify-center gap-2 p-5 rounded-2xl border-2 transition-all ${
                  feedbackType === type ? activeClass : 'bg-white text-slate-700 border-slate-200'
                }`}
                style={{ minHeight: '100px' }}
              >
                <Icon className="w-8 h-8" />
                <span className="font-semibold text-sm">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Avaliação */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-5">Avaliação</h2>
          <div className="space-y-5">
            {/* Produtos */}
            <StarRating label="Produtos" value={ratingFood} onChange={setRatingFood} secondaryColor={secondaryColor} />
            {showFoodTags && (
              <div className="animate-in fade-in slide-in-from-top-1 duration-200 pl-1" data-testid="tags-produto">
                <p className="text-xs font-medium text-red-500 mb-2">{TAG_CATEGORIES.produto.label} <span className="text-slate-400 font-normal">(opcional)</span></p>
                <div className="flex flex-wrap gap-1.5">
                  {TAG_CATEGORIES.produto.tags.map(tag => (
                    <button key={tag} type="button" onClick={() => toggleTag(tag)}
                      data-testid={`tag-${tag.toLowerCase().replace(/[\s/]+/g, '-')}`}
                      className={`px-3 py-2 rounded-full text-xs font-medium transition-all active:scale-95 ${
                        complaintTags.includes(tag) ? 'bg-red-500 text-white shadow-sm' : 'bg-slate-100 text-slate-600'
                      }`}>{tag}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Atendimento */}
            <StarRating label="Atendimento" value={ratingService} onChange={setRatingService} secondaryColor={secondaryColor} />
            {showServiceTags && (
              <div className="animate-in fade-in slide-in-from-top-1 duration-200 pl-1" data-testid="tags-atendimento">
                <p className="text-xs font-medium text-red-500 mb-2">{TAG_CATEGORIES.atendimento.label} <span className="text-slate-400 font-normal">(opcional)</span></p>
                <div className="flex flex-wrap gap-1.5">
                  {TAG_CATEGORIES.atendimento.tags.map(tag => (
                    <button key={tag} type="button" onClick={() => toggleTag(tag)}
                      data-testid={`tag-${tag.toLowerCase().replace(/[\s/]+/g, '-')}`}
                      className={`px-3 py-2 rounded-full text-xs font-medium transition-all active:scale-95 ${
                        complaintTags.includes(tag) ? 'bg-red-500 text-white shadow-sm' : 'bg-slate-100 text-slate-600'
                      }`}>{tag}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Ambiente */}
            <StarRating label="Ambiente" value={ratingAmbiance} onChange={setRatingAmbiance} secondaryColor={secondaryColor} />
            {showAmbianceTags && (
              <div className="animate-in fade-in slide-in-from-top-1 duration-200 pl-1" data-testid="tags-ambiente">
                <p className="text-xs font-medium text-red-500 mb-2">{TAG_CATEGORIES.ambiente.label} <span className="text-slate-400 font-normal">(opcional)</span></p>
                <div className="flex flex-wrap gap-1.5">
                  {TAG_CATEGORIES.ambiente.tags.map(tag => (
                    <button key={tag} type="button" onClick={() => toggleTag(tag)}
                      data-testid={`tag-${tag.toLowerCase().replace(/[\s/]+/g, '-')}`}
                      className={`px-3 py-2 rounded-full text-xs font-medium transition-all active:scale-95 ${
                        complaintTags.includes(tag) ? 'bg-red-500 text-white shadow-sm' : 'bg-slate-100 text-slate-600'
                      }`}>{tag}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mensagem */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Sua Mensagem</h2>
          <Textarea
            data-testid="input-message"
            placeholder="Conte-nos mais detalhes..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="resize-none text-base"
          />
          
          <div className="mt-4">
            {/* Photo Thumbnails */}
            {photoUrls.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-3" data-testid="photo-preview-area">
                {photoUrls.map((url, idx) => (
                  <div key={url} className="relative w-24 h-24 rounded-xl overflow-hidden border-2 border-emerald-200 bg-emerald-50 shrink-0">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                      alt={`Foto ${idx + 1}`}
                      className="w-full h-full object-cover"
                      data-testid={`photo-preview-img-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(idx)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                      data-testid={`photo-remove-btn-${idx}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Upload Button */}
            {photoUrls.length < 3 && (
              <div className="flex gap-2">
                <Label htmlFor="photo-camera" className="cursor-pointer flex-1">
                  <div
                    className={`flex items-center justify-center gap-2 py-4 px-3 border-2 border-dashed rounded-xl transition-colors ${
                      uploading ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="photo-camera-area"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                        <span className="text-sm text-blue-600 font-medium">Enviando...</span>
                      </>
                    ) : (
                      <>
                        <Camera className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-slate-500">Tirar Foto</span>
                      </>
                    )}
                  </div>
                </Label>
                <Label htmlFor="photo-gallery" className="cursor-pointer flex-1">
                  <div
                    className={`flex items-center justify-center gap-2 py-4 px-3 border-2 border-dashed rounded-xl transition-colors ${
                      uploading ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="photo-gallery-area"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                        <span className="text-sm text-blue-600 font-medium">Enviando...</span>
                      </>
                    ) : (
                      <>
                        <Images className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-slate-500">Galeria ({3 - photoUrls.length})</span>
                      </>
                    )}
                  </div>
                </Label>
              </div>
            )}
            <input
              id="photo-camera"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoUpload}
              className="hidden"
              disabled={uploading || photoUrls.length >= 3}
              data-testid="photo-camera-input"
            />
            <input
              id="photo-gallery"
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
              disabled={uploading || photoUrls.length >= 3}
              data-testid="photo-gallery-input"
            />
          </div>
        </div>

        {/* Botão Enviar */}
        <div className="pb-6">
          <Button
            type="submit"
            disabled={!canSubmit || submitting}
            data-testid="submit-feedback-btn"
            className="w-full h-14 text-base font-semibold rounded-2xl shadow-lg transition-all"
            style={{ 
              backgroundColor: canSubmit ? secondaryColor : '#9ca3af',
              color: canSubmit ? primaryColor : '#fff'
            }}
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Enviando...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <Send className="w-5 h-5 mr-2" />
                Enviar Avaliação
              </span>
            )}
          </Button>
        </div>
      </form>
      <div className="mt-8 pb-6 text-center">
        <p className="text-xs text-slate-400">
          Tecnologia{' '}
          <a href="https://falocomodono.com.br" target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:underline font-medium">
            Falo Com O Dono
          </a>
        </p>
      </div>
    </div>
  );
};

export default FeedbackForm;
